import { useCallback, useEffect, useState } from 'react';

import { IPreloadImageParams, preloadImage } from '../utils/preloadImage';

export interface IUseImagePreloaderProps extends Partial<IPreloadImageParams> {
  hasManualPreloading?: boolean;
}

export const useImagePreloader = ({
  hasManualPreloading = false,
  src,
}: IUseImagePreloaderProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(!src);
  const [hasError, setHasError] = useState(false);

  const handleImagePreload = useCallback(async () => {
    if (src) {
      try {
        setIsLoading(true);

        await preloadImage({ src });

        setIsLoading(false);
        setIsLoaded(true);
        setHasError(false);
      } catch {
        setIsLoading(false);
        setHasError(true);
      }
    }
  }, [src]);

  useEffect(() => {
    if (!src) {
      setIsLoaded(true);

      return () => {};
    }

    if (src && !hasManualPreloading) {
      let isAborted = false;

      setIsLoading(true);
      preloadImage({ src })
        .then(() => {
          if (!isAborted) {
            setIsLoading(false);
            setIsLoaded(true);
            setHasError(false);
          }
        })
        .catch(() => {
          if (!isAborted) {
            setIsLoading(false);
            setIsLoaded(false);
            setHasError(true);
          }
        });

      return () => {
        setIsLoading(false);

        isAborted = true;
      };
    }

    return () => {};
  }, [hasManualPreloading, src]);

  return { handleImagePreload, hasError, isLoaded, isLoading };
};
